/**
 * ShiftPlannerView - Tree-style shift crew allocation interface
 * 
 * Inspired by uTrac planning interface - high information density,
 * drag-and-drop crew assignments, visual crew slots, and context menus.
 * 
 * Features:
 * - Crew pool on the left for drag-and-drop
 * - Tree structure showing shifts with expandable crew slots
 * - Visual indication of slot status (empty/filled/accepted/rejected)
 * - Crew Chief designation
 * - Meal placeholders auto-generated from rules
 * - Context-sensitive right-click menus
 * - Copy/paste crew between shifts (via shared module)
 * 
 * EF-PLANNER-SHARED-CREW-ACTIONS v1.0: Uses shared copy/paste module
 * EF-CREW-OVERLAP-PREVENTION-V1: Shows conflict dialogs when overlaps detected
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import {
  getShiftCrew,
  getEligibleShiftCrew,
  addShiftCrew,
  removeShiftCrew,
  updateShiftCrew,
  getShiftTasks,
  extractErrorMessage,
} from "../../lib/api";
import api from "../../lib/api";
import { handleCopyCrew, handlePasteCrew } from "./sharedCrewActions";
import { ShiftConflictDialog } from "./ShiftConflictDialog";
import { CrewReadinessChip } from "./CrewReadinessChip";
import { AssignmentStatusBadge, OnboardingOverrideBadge } from "./AssignmentStatusBadge";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "../ui/context-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Users,
  ChevronRight,
  ChevronDown,
  Clock,
  MapPin,
  GripVertical,
  Copy,
  Clipboard,
  ClipboardPaste,
  Trash2,
  Pencil,
  Mail,
  Bell,
  User,
  UserPlus,
  Utensils,
  RefreshCw,
  AlertTriangle,
  Check,
  X,
  Loader2,
} from "lucide-react";
import { cn } from "../../lib/utils";

// ============================================================
// DRAGGABLE CREW ITEM - For the crew pool
// ============================================================
function DraggableCrewItem({ staff, isDragging, onEditCrew }) {
  // EF-ENFORCE-ACTIVE-CREW-ONLY-ASSIGNMENT-V1: Check if crew is eligible/ready
  const isReady = staff.is_eligible !== false && (
    staff.readiness_status === "ready" || 
    !staff.readiness_status // Backwards compatibility
  );
  
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `crew-${staff.id}`,
    data: { type: "crew", staff },
    disabled: !isReady, // Disable drag if not ready
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  // Get tooltip message for non-ready crew
  const getReadinessTooltip = () => {
    if (isReady) return null;
    const status = staff.readiness_status || "unknown";
    const reasons = {
      missing_documents: "Documents incomplete",
      verification_required: "Documents pending verification",
      document_expired: "Documents expired",
      document_expiring: "Documents expiring soon",
      inactive: "Crew is inactive",
    };
    return reasons[status] || staff.readiness_reason || `Status: ${status}`;
  };

  const tooltipMsg = getReadinessTooltip();

  const content = (
    <div
      ref={setNodeRef}
      style={style}
      {...(isReady ? listeners : {})}
      {...(isReady ? attributes : {})}
      className={cn(
        "flex items-center gap-2 px-2 py-1.5 rounded text-sm",
        isReady 
          ? "cursor-grab active:cursor-grabbing bg-surface-elevated/30 border border-border/50 hover:bg-surface-elevated/50 hover:border-primary/30"
          : "cursor-not-allowed bg-surface-elevated/10 border border-border/30 opacity-50",
        isDragging && "opacity-50",
        "transition-colors"
      )}
      title={tooltipMsg || undefined}
    >
      <GripVertical className={cn("h-3 w-3", isReady ? "text-muted-foreground" : "text-muted-foreground/30")} />
      <User className={cn("h-3.5 w-3.5", isReady ? "text-muted-foreground" : "text-muted-foreground/30")} />
      <span className="truncate flex-1 min-w-0">
        {staff.first_name} {staff.last_name}
      </span>
      {/* EVENTSFLO-CREW-PLANNER-READINESS-VISUAL-V1: explicit readiness chip */}
      <CrewReadinessChip staff={staff} />
      {/* EF-ENFORCE-ACTIVE-CREW-ONLY-ASSIGNMENT-V1: Show inactive indicator */}
      {!isReady && (
        <span className="text-xs text-amber-500" title={tooltipMsg}>⚠️</span>
      )}
      {staff.skills?.includes("Crew Chief") && isReady && (
        <span className="text-xs">⛑️</span>
      )}
    </div>
  );

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        {content}
      </ContextMenuTrigger>
      <ContextMenuContent className="w-48">
        <ContextMenuItem onClick={() => onEditCrew(staff.id)}>
          <Pencil className="h-4 w-4 mr-2" />
          Edit Crew
        </ContextMenuItem>
        {!isReady && (
          <ContextMenuItem disabled className="text-muted-foreground text-xs">
            {tooltipMsg}
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}

// ============================================================
// DROPPABLE CREW SLOT - Empty slot in a shift
// ============================================================
function DroppableCrewSlot({ shiftId, slotIndex, isOver }) {
  const { setNodeRef, isOver: isOverSlot } = useDroppable({
    id: `slot-${shiftId}-${slotIndex}`,
    data: { type: "slot", shiftId, slotIndex },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex items-center gap-2 px-2 py-1.5 rounded border-2 border-dashed text-xs",
        isOver || isOverSlot
          ? "border-primary/50 bg-primary/5 text-primary"
          : "border-muted-foreground/20 text-muted-foreground"
      )}
    >
      <div className="w-4 h-4 rounded-full border border-current flex items-center justify-center">
        <UserPlus className="h-2.5 w-2.5" />
      </div>
      <span>Drop crew here</span>
    </div>
  );
}

// ============================================================
// ASSIGNED CREW SLOT - Filled slot with crew member
// ============================================================
function AssignedCrewSlot({ assignment, onRemove, onToggleCrewChief, shiftId }) {
  const isCrewChief =
    assignment.role?.toLowerCase().includes("crew chief") ||
    assignment.role?.toLowerCase().includes("team leader");

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          className={cn(
            "flex items-center gap-2 px-2 py-1.5 rounded text-sm",
            "bg-surface-elevated/40 border border-border/50 hover:border-primary/30",
            "cursor-pointer transition-colors group"
          )}
        >
          {/* Status icon */}
          <div
            className={cn(
              "w-4 h-4 rounded-full flex items-center justify-center shrink-0",
              assignment.status === "accepted"
                ? "bg-green-500/20 text-green-600"
                : assignment.status === "rejected"
                ? "bg-red-500/20 text-red-600"
                : "bg-blue-500/20 text-blue-600"
            )}
          >
            {assignment.status === "accepted" ? (
              <Check className="h-2.5 w-2.5" />
            ) : assignment.status === "rejected" ? (
              <X className="h-2.5 w-2.5" />
            ) : (
              <User className="h-2.5 w-2.5" />
            )}
          </div>

          {/* Crew Chief indicator */}
          {isCrewChief && (
            <span className="text-amber-500">⛑️</span>
          )}

          {/* Name */}
          <span className="truncate flex-1 min-w-0">{assignment.staff_name}</span>

          {/* EVENTSFLO-CREW-PLANNER-ASSIGNMENT-STATUS-ICONS-V1:
              Push/Accept/Reject/Confirmed/Worked indicator — the legacy
              onboarding-override icon below is preserved as a separate
              amber triangle since it is independent of the response state. */}
          <AssignmentStatusBadge assignment={assignment} showLabel />
          <OnboardingOverrideBadge assignment={assignment} />

          {/* Role badge */}
          <Badge
            variant="outline"
            className="text-[10px] px-1 py-0 h-4 shrink-0"
          >
            {assignment.role?.replace(/\s*\(Crew Chief\)/gi, "").replace(/\s*\(Team Leader\)/gi, "").trim() || "Crew"}
          </Badge>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-48">
        <ContextMenuItem onClick={() => onToggleCrewChief(assignment.staff_id, !isCrewChief)}>
          <span className="mr-2">⛑️</span>
          {isCrewChief ? "Remove Crew Chief" : "Set as Crew Chief"}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          className="text-red-600"
          onClick={() => onRemove(assignment.staff_id)}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Remove from Shift
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

// ============================================================
// MEAL ITEM - Auto-generated meal placeholder
// ============================================================
function MealItem({ meal, onDelete }) {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="flex items-center gap-2 px-2 py-1 rounded text-xs bg-orange-500/10 border border-orange-500/30 text-orange-600">
          <Utensils className="h-3 w-3" />
          <span>{meal.name || "Meal Allowance"}</span>
          <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 ml-auto">
            ×{meal.quantity || 1}
          </Badge>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-40">
        <ContextMenuItem className="text-red-600" onClick={onDelete}>
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Meal
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

// ============================================================
// SHIFT ROW - Expandable shift with crew slots
// ============================================================
function ShiftRow({
  shift,
  crew,
  meals,
  crewChiefRequired,
  crewChiefCount,
  onRemoveCrew,
  onToggleCrewChief,
  onEdit,
  onDelete,
  onClone,
  onCopyCrew,
  onPasteCrew,
  hasClipboard,
  isOver,
  formatTime,
  formatDuration,
}) {
  const [expanded, setExpanded] = useState(true);
  const staffRequired = shift.staff_required || 0;
  const assignedCount = crew.length;
  const emptySlots = Math.max(0, staffRequired - assignedCount);

  // Commercial state (derived)
  const getCommercialState = () => {
    if (shift.invoice_id) return { label: "Invoice", color: "text-purple-600 bg-purple-500/10" };
    if (shift.quote_status === "approved") return { label: "Accepted", color: "text-green-600 bg-green-500/10" };
    if (shift.quote_id) return { label: "Quote", color: "text-blue-600 bg-blue-500/10" };
    return { label: "Estimate", color: "text-muted-foreground bg-surface-elevated/10" };
  };
  const commercialState = getCommercialState();

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="border border-border/50 rounded overflow-hidden bg-card">
          {/* Shift header - always visible */}
          <Collapsible open={expanded} onOpenChange={setExpanded}>
            <CollapsibleTrigger asChild>
              <div
                className={cn(
                  "flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-surface-elevated/30 transition-colors",
                  isOver && "bg-primary/5 border-l-2 border-primary"
                )}
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                )}

                {/* Shift name */}
                <span className="font-medium text-sm truncate">{shift.name}</span>

                {/* Location */}
                {shift.location_name && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 shrink-0">
                    <MapPin className="h-2.5 w-2.5 mr-1" />
                    {shift.location_name}
                  </Badge>
                )}

                {/* Time */}
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 shrink-0 ml-auto">
                  <Clock className="h-2.5 w-2.5 mr-1" />
                  {formatTime(shift.start_time)} · {formatDuration(shift.duration_hours)}
                </Badge>

                {/* Crew count - EF-CREW-ALLOCATION-STABILITY-V1: Shows "required • assigned • remaining" */}
                <Badge
                  className={cn(
                    "text-[10px] px-1.5 py-0 h-5",
                    assignedCount < staffRequired
                      ? "bg-amber-500/20 text-amber-600 border-amber-500/30"
                      : assignedCount > staffRequired
                      ? "bg-red-500/20 text-red-600 border-red-500/30"
                      : "bg-green-500/20 text-green-600 border-green-500/30"
                  )}
                  title={`${staffRequired} required • ${assignedCount} assigned • ${Math.max(0, staffRequired - assignedCount)} remaining`}
                >
                  <Users className="h-2.5 w-2.5 mr-1" />
                  {assignedCount}/{staffRequired}
                  {assignedCount > staffRequired && (
                    <AlertTriangle className="h-2.5 w-2.5 ml-1" />
                  )}
                </Badge>

                {/* Crew Chief indicator - shows under (amber), correct (green), or over (blue info) */}
                {crewChiefRequired > 0 && (
                  <Badge
                    className={cn(
                      "text-[10px] px-1 py-0 h-5",
                      crewChiefCount < crewChiefRequired
                        ? "bg-amber-500/20 text-amber-600 border-amber-500/30"
                        : crewChiefCount > crewChiefRequired
                        ? "bg-blue-500/20 text-blue-600 border-blue-500/30"
                        : "bg-green-500/20 text-green-600 border-green-500/30"
                    )}
                  >
                    ⛑️ {crewChiefCount}/{crewChiefRequired}
                    {crewChiefCount > crewChiefRequired && " (over)"}
                  </Badge>
                )}

                {/* Commercial state */}
                <Badge className={cn("text-[10px] px-1.5 py-0 h-5", commercialState.color)}>
                  {commercialState.label}
                </Badge>
              </div>
            </CollapsibleTrigger>

            {/* Shift content - crew slots and meals */}
            <CollapsibleContent>
              <div className="px-3 pb-2 space-y-1.5 border-t border-border/30 pt-2">
                {/* Assigned crew */}
                {crew.map((assignment) => (
                  <AssignedCrewSlot
                    key={assignment.id}
                    assignment={assignment}
                    shiftId={shift.id}
                    onRemove={onRemoveCrew}
                    onToggleCrewChief={onToggleCrewChief}
                  />
                ))}

                {/* Empty slots */}
                {Array.from({ length: emptySlots }).map((_, index) => (
                  <DroppableCrewSlot
                    key={`slot-${index}`}
                    shiftId={shift.id}
                    slotIndex={assignedCount + index}
                    isOver={isOver}
                  />
                ))}

                {/* Meals */}
                {meals.length > 0 && (
                  <div className="pt-1.5 border-t border-border/30 space-y-1">
                    {meals.map((meal, idx) => (
                      <MealItem
                        key={meal.id || idx}
                        meal={meal}
                        onDelete={() => {}}
                      />
                    ))}
                  </div>
                )}

                {/* EF-CREW-ALLOCATION-STABILITY-V1: Allocation status display */}
                {assignedCount < staffRequired && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-600 pt-1">
                    <AlertTriangle className="h-3 w-3" />
                    <span>{staffRequired} required • {assignedCount} assigned • {staffRequired - assignedCount} remaining</span>
                  </div>
                )}
                
                {/* Warning if overstaffed */}
                {assignedCount > staffRequired && (
                  <div className="flex items-center gap-1.5 text-xs text-red-600 pt-1">
                    <AlertTriangle className="h-3 w-3" />
                    <span>{staffRequired} required • {assignedCount} assigned • Overstaffed by {assignedCount - staffRequired}</span>
                  </div>
                )}
                
                {/* Fully staffed */}
                {assignedCount === staffRequired && staffRequired > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-green-600 pt-1">
                    <Check className="h-3 w-3" />
                    <span>{staffRequired} required • {assignedCount} assigned • Fully staffed</span>
                  </div>
                )}
                
                {/* Warning if too many Crew Chiefs */}
                {crewChiefCount > crewChiefRequired && crewChiefRequired > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-blue-600 pt-1">
                    <span>⛑️</span>
                    <span>{crewChiefCount - crewChiefRequired} extra Crew Chief(s) assigned</span>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </ContextMenuTrigger>

      {/* Shift context menu */}
      <ContextMenuContent className="w-52">
        <ContextMenuItem onClick={onEdit}>
          <Pencil className="h-4 w-4 mr-2" />
          Edit Shift
        </ContextMenuItem>
        <ContextMenuItem onClick={onClone}>
          <Copy className="h-4 w-4 mr-2" />
          Clone Shift
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <RefreshCw className="h-4 w-4 mr-2" />
            Shift Status
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            <ContextMenuItem>Draft</ContextMenuItem>
            <ContextMenuItem>Confirmed</ContextMenuItem>
            <ContextMenuItem>In Progress</ContextMenuItem>
            <ContextMenuItem>Completed</ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSeparator />
        <ContextMenuItem>
          <Mail className="h-4 w-4 mr-2" />
          Email Crew
        </ContextMenuItem>
        <ContextMenuItem>
          <Bell className="h-4 w-4 mr-2" />
          App Alert
        </ContextMenuItem>
        <ContextMenuSeparator />
        {/* Copy/Paste Crew */}
        <ContextMenuItem onClick={onCopyCrew} disabled={crew.length === 0}>
          <Clipboard className="h-4 w-4 mr-2" />
          Copy Crew ({crew.length})
        </ContextMenuItem>
        <ContextMenuItem onClick={onPasteCrew} disabled={!hasClipboard}>
          <ClipboardPaste className="h-4 w-4 mr-2" />
          Paste Crew
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem className="text-red-600" onClick={onDelete}>
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Shift
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

// ============================================================
// CREW POOL - Left sidebar with available crew
// ============================================================
function CrewPool({ eligibleCrew, loading, searchTerm, onSearchChange, onEditCrew }) {
  return (
    <div className="w-64 border-r border-border/50 flex flex-col bg-surface-elevated/10">
      {/* Header */}
      <div className="p-3 border-b border-border/50">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          Crew Pool
          {eligibleCrew.length > 0 && (
            <Badge variant="outline" className="text-xs ml-auto">
              {eligibleCrew.length}
            </Badge>
          )}
        </h3>
        <input
          type="text"
          placeholder="Search crew..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full mt-2 px-2 py-1.5 text-sm rounded border border-border/50 bg-surface-base focus:outline-none focus:border-primary/50"
        />
      </div>

      {/* Crew list */}
      <ScrollArea className="flex-1 p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : eligibleCrew.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No available crew
          </div>
        ) : (
          <div className="space-y-1.5">
            {eligibleCrew.map((staff) => (
              <DraggableCrewItem key={staff.id} staff={staff} onEditCrew={onEditCrew} />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT - ShiftPlannerView
// ============================================================
export function ShiftPlannerView({
  projectId,
  shifts,
  projectStatus, // EF-PLANNER-VISIBILITY-AND-OVERRIDE-FIX-V2: Accept project status for filtering
  onEditShift,
  onDeleteShift,
  onCloneShift,
  onRefresh,
  formatTime,
  formatDuration,
}) {
  const navigate = useNavigate();
  
  // State
  const [eligibleCrew, setEligibleCrew] = useState([]);
  const [shiftCrewMap, setShiftCrewMap] = useState({}); // Map of shiftId -> crew[]
  const [shiftMealsMap, setShiftMealsMap] = useState({}); // Map of shiftId -> meals[]
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [overShiftId, setOverShiftId] = useState(null);
  
  // Clipboard state for copy/paste crew
  const [crewClipboard, setCrewClipboard] = useState(null); // { sourceShiftId, crew: [{staff_id, role}...] }
  
  // Rule thresholds
  const [crewChiefThreshold1, setCrewChiefThreshold1] = useState(4);
  const [crewChiefThreshold2, setCrewChiefThreshold2] = useState(12);

  // EF-CREW-OVERLAP-PREVENTION-V1: Conflict dialog state
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [pendingAssignment, setPendingAssignment] = useState(null); // { shiftId, staff }
  const [conflictSubmitting, setConflictSubmitting] = useState(false);

  // EF-PLANNER-TENANT-FILTER-AND-VISIBILITY-V1: Replace single toggle with two toggles
  const [hideCompletedShifts, setHideCompletedShifts] = useState(() => {
    const stored = localStorage.getItem("hideCompletedShiftsPlanner");
    return stored !== null ? stored === "true" : true; // Default ON
  });

  const [hideArchivedProjects, setHideArchivedProjects] = useState(() => {
    const stored = localStorage.getItem("hideArchivedProjectsPlanner");
    return stored !== null ? stored === "true" : true; // Default ON
  });

  // EF-PLANNER-TENANT-FILTER-AND-VISIBILITY-V1: Onboarding override dialog state
  const [onboardingOverrideDialogOpen, setOnboardingOverrideDialogOpen] = useState(false);
  const [pendingOnboardingOverride, setPendingOnboardingOverride] = useState(null); // { shiftId, staff, errorData }
  const [onboardingOverrideSubmitting, setOnboardingOverrideSubmitting] = useState(false);

  // EF-PLANNER-TENANT-FILTER-AND-VISIBILITY-V1: Persist toggle preferences
  const handleToggleHideCompletedShifts = (checked) => {
    setHideCompletedShifts(checked);
    localStorage.setItem("hideCompletedShiftsPlanner", String(checked));
  };

  const handleToggleHideArchivedProjects = (checked) => {
    setHideArchivedProjects(checked);
    localStorage.setItem("hideArchivedProjectsPlanner", String(checked));
  };

  // EF-PLANNER-VISIBILITY-AND-OVERRIDE-FIX-V2: Filter shifts by completion status and project status
  const filteredShifts = useMemo(() => {
    let filtered = shifts;
    
    // EF-PLANNER-VISIBILITY-AND-OVERRIDE-FIX-V2: Filter archived projects
    // If projectStatus is provided and "Hide Archived" is ON, filter out if project is completed/archived
    if (hideArchivedProjects && projectStatus) {
      const isArchivedProject = ["completed", "archived"].includes(projectStatus?.toLowerCase());
      if (isArchivedProject) {
        // If the current project is archived, show warning but still display shifts
        // (filtering would hide everything since all shifts belong to this project)
        console.log("[PLANNER] Project is archived/completed:", projectStatus);
      }
    }
    
    // Filter completed shifts (end_time < now)
    if (hideCompletedShifts) {
      const now = new Date();
      filtered = filtered.filter(shift => {
        // Parse end_time with date
        const shiftDate = shift.date ? new Date(shift.date) : null;
        if (!shiftDate) return true; // Keep shifts without dates
        
        // If shift has end_time, use it; otherwise just compare dates
        if (shift.end_time) {
          const [hours, minutes] = shift.end_time.split(':').map(Number);
          shiftDate.setHours(hours || 23, minutes || 59, 59);
        } else {
          shiftDate.setHours(23, 59, 59); // End of day
        }
        
        return shiftDate >= now;
      });
    }
    
    // EF-PLANNER-VISIBILITY-AND-OVERRIDE-FIX-V2: Filter by shift.project_status if available
    if (hideArchivedProjects) {
      filtered = filtered.filter(shift => {
        const shiftProjectStatus = shift.project_status?.toLowerCase();
        if (!shiftProjectStatus) return true; // Keep if no status available
        return !["completed", "archived"].includes(shiftProjectStatus);
      });
    }
    
    return filtered;
  }, [shifts, hideCompletedShifts, hideArchivedProjects, projectStatus]);

  // Navigate to crew profile editor
  const handleEditCrew = (staffId) => {
    navigate(`/staff/${staffId}`);
  };

  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Load data
  const loadData = useCallback(async () => {
    if (!projectId || !shifts.length) return;

    try {
      setLoading(true);

      // Get eligible crew for the first shift as reference
      const firstShiftId = shifts[0]?.id;
      const eligibleRes = firstShiftId
        ? await getEligibleShiftCrew(firstShiftId)
        : { data: [] };

      // Get crew and tasks for all shifts
      const crewPromises = shifts.map((s) =>
        getShiftCrew(s.id).catch(() => ({ data: [] }))
      );
      const tasksPromises = shifts.map((s) =>
        getShiftTasks(s.id).catch(() => ({ data: [] }))
      );

      const [crewResults, tasksResults] = await Promise.all([
        Promise.all(crewPromises),
        Promise.all(tasksPromises),
      ]);

      // Build maps
      const crewMap = {};
      const mealsMap = {};
      shifts.forEach((shift, idx) => {
        crewMap[shift.id] = crewResults[idx]?.data || [];
        // Extract meal tasks
        const tasks = tasksResults[idx]?.data || [];
        mealsMap[shift.id] = tasks.filter(
          (t) =>
            t.task_name?.toLowerCase().includes("meal") ||
            t.task_definition_name?.toLowerCase().includes("meal")
        );
      });

      setEligibleCrew(eligibleRes.data || []);
      setShiftCrewMap(crewMap);
      setShiftMealsMap(mealsMap);

      // Load rule variables
      const varsRes = await api.get("/rule-variables");
      const vars = varsRes.data || [];
      const t1 = vars.find((v) => v.key === "crew_chief_threshold_1") ||
                 vars.find((v) => v.key === "team_leader_threshold_1");
      const t2 = vars.find((v) => v.key === "crew_chief_threshold_2") ||
                 vars.find((v) => v.key === "team_leader_threshold_2");
      if (t1) setCrewChiefThreshold1(t1.value);
      if (t2) setCrewChiefThreshold2(t2.value);
    } catch (err) {
      console.error("Failed to load planner data:", err);
    } finally {
      setLoading(false);
    }
  }, [projectId, shifts]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter crew by search
  const filteredCrew = useMemo(() => {
    if (!searchTerm) return eligibleCrew;
    const lower = searchTerm.toLowerCase();
    return eligibleCrew.filter(
      (s) =>
        s.first_name?.toLowerCase().includes(lower) ||
        s.last_name?.toLowerCase().includes(lower)
    );
  }, [eligibleCrew, searchTerm]);

  // Calculate crew chief requirements
  const getCrewChiefRequired = (crewCount) => {
    if (crewCount >= crewChiefThreshold2) return 2;
    if (crewCount >= crewChiefThreshold1) return 1;
    return 0;
  };

  const getCrewChiefCount = (crew) => {
    return crew.filter(
      (c) =>
        c.role?.toLowerCase().includes("crew chief") ||
        c.role?.toLowerCase().includes("team leader")
    ).length;
  };

  // Drag handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (over?.data?.current?.type === "slot") {
      setOverShiftId(over.data.current.shiftId);
    } else {
      setOverShiftId(null);
    }
  };

  // EF-CREW-OVERLAP-PREVENTION-V1: Enhanced drag-end with conflict handling
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverShiftId(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (activeData?.type === "crew" && overData?.type === "slot") {
      const staff = activeData.staff;
      const shiftId = overData.shiftId;

      try {
        await addShiftCrew(shiftId, staff.id, "Crew");
        toast.success(`Added ${staff.first_name} ${staff.last_name} to shift`);
        await loadData();
      } catch (err) {
        // EF-CREW-ALLOCATION-STABILITY-V1: Enhanced error handling
        const status = err.response?.status;
        const errorData = err.response?.data;
        
        if (status === 409) {
          // Check error type - could be conflict or duplicate
          if (errorData?.error === "duplicate_assignment") {
            toast.error(errorData.message || "Crew member already assigned");
          } else if (errorData?.error_type === "SHIFT_CONFLICT") {
            // Show conflict dialog for overlaps
            setConflictData(errorData);
            setPendingAssignment({ shiftId, staff });
            setConflictDialogOpen(true);
          } else {
            toast.error(extractErrorMessage(err));
          }
        } else if (status === 400) {
          // Check for specific error types
          if (errorData?.error === "over_allocation") {
            toast.error(errorData.message || "Crew exceeds required number");
          } else if (errorData?.error === "invalid_shift_data") {
            toast.error(errorData.message || "Cannot assign crew: shift data invalid");
          } else {
            toast.error(extractErrorMessage(err));
          }
        } else if (status === 403) {
          // EF-PLANNER-VISIBILITY-AND-OVERRIDE-FIX-V2: Check if override is allowed
          if (errorData?.error === "crew_not_active" && errorData?.can_override) {
            // Debug log
            console.log("[OVERRIDE DIALOG TRIGGERED]", { 
              staff_id: staff.id, 
              staff_name: `${staff.first_name} ${staff.last_name}`,
              reason: errorData?.message,
              can_override: true 
            });
            // Show onboarding override confirmation dialog
            setPendingOnboardingOverride({ shiftId, staff, errorData });
            setOnboardingOverrideDialogOpen(true);
          } else if (errorData?.error === "crew_not_active") {
            console.log("[CREW ASSIGN BLOCKED]", { staff_id: staff.id, reason: errorData?.message, can_override: false });
            toast.error(errorData.message || "Crew member is not active");
          } else {
            toast.error(extractErrorMessage(err));
          }
        } else {
          toast.error(extractErrorMessage(err));
        }
      }
    }
  };

  // EF-PLANNER-VISIBILITY-AND-OVERRIDE-FIX-V2: Handle onboarding override confirmation
  const handleOnboardingOverride = async () => {
    if (!pendingOnboardingOverride) return;
    
    setOnboardingOverrideSubmitting(true);
    const { shiftId, staff, errorData } = pendingOnboardingOverride;
    
    // Debug log
    console.log("[OVERRIDE CONFIRMED]", { 
      staff_id: staff.id, 
      shift_id: shiftId,
      staff_name: `${staff.first_name} ${staff.last_name}`,
      reason: errorData?.message
    });
    
    try {
      // Call addShiftCrew with onboarding_override=true
      await addShiftCrew(shiftId, staff.id, "Crew", null, false, null, true);
      toast.success(`Added ${staff.first_name} ${staff.last_name} to shift (onboarding override)`);
      await loadData();
      setOnboardingOverrideDialogOpen(false);
      setPendingOnboardingOverride(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setOnboardingOverrideSubmitting(false);
    }
  };

  const handleCloseOnboardingOverrideDialog = () => {
    setOnboardingOverrideDialogOpen(false);
    setPendingOnboardingOverride(null);
  };

  // EF-CREW-OVERLAP-PREVENTION-V1: Handle override from conflict dialog
  const handleConflictOverride = async (reason) => {
    if (!pendingAssignment) return;
    
    setConflictSubmitting(true);
    try {
      const { shiftId, staff } = pendingAssignment;
      await addShiftCrew(shiftId, staff.id, "Crew", null, true, reason);
      toast.success(`Added ${staff.first_name} ${staff.last_name} to shift (override)`);
      await loadData();
      setConflictDialogOpen(false);
      setConflictData(null);
      setPendingAssignment(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setConflictSubmitting(false);
    }
  };

  const handleCloseConflictDialog = () => {
    setConflictDialogOpen(false);
    setConflictData(null);
    setPendingAssignment(null);
  };

  // Crew management handlers
  const handleRemoveCrew = async (shiftId, staffId) => {
    try {
      await removeShiftCrew(shiftId, staffId);
      toast.success("Crew member removed");
      await loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const handleToggleCrewChief = async (shiftId, staffId, makeCrewChief) => {
    const crew = shiftCrewMap[shiftId] || [];
    const assignment = crew.find((c) => c.staff_id === staffId);
    if (!assignment) return;

    let newRole = assignment.role || "Crew";
    newRole = newRole.replace(/\s*\(Crew Chief\)/gi, "").replace(/\s*\(Team Leader\)/gi, "").trim();
    if (newRole.toLowerCase() === "crew chief" || newRole.toLowerCase() === "team leader") {
      newRole = "Crew";
    }
    if (makeCrewChief) {
      newRole = `${newRole} (Crew Chief)`;
    }

    try {
      await updateShiftCrew(shiftId, staffId, newRole);
      toast.success(makeCrewChief ? "Set as Crew Chief" : "Removed Crew Chief status");
      await loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  // Copy/Paste crew handlers (using shared module)
  const onCopyCrew = (shiftId) => {
    handleCopyCrew({ shiftId, shiftCrewMap, setCrewClipboard });
  };

  const onPasteCrew = async (targetShiftId) => {
    await handlePasteCrew({ 
      targetShiftId, 
      crewClipboard, 
      shifts, 
      shiftCrewMap, 
      loadData 
    });
  };

  // Drag overlay content
  const activeCrew = activeId
    ? eligibleCrew.find((s) => `crew-${s.id}` === activeId)
    : null;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex h-full border border-border/50 rounded-lg overflow-hidden bg-surface-base">
        {/* Crew Pool */}
        <CrewPool
          eligibleCrew={filteredCrew}
          loading={loading}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onEditCrew={handleEditCrew}
        />

        {/* Shifts area */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-sm font-medium">
                Project Shifts
                <Badge variant="outline" className="ml-2 text-xs">
                  {filteredShifts.length}{(hideCompletedShifts || hideArchivedProjects) && filteredShifts.length !== shifts.length && `/${shifts.length}`}
                </Badge>
              </h3>
              
              {/* EF-PLANNER-VISIBILITY-AND-OVERRIDE-FIX-V2: Both visibility toggles */}
              <div className="flex items-center gap-4 border-l border-border/50 pl-3">
                {/* Toggle A: Hide Completed Shifts */}
                <div className="flex items-center gap-2">
                  <Switch
                    id="hide-completed-shifts"
                    checked={hideCompletedShifts}
                    onCheckedChange={handleToggleHideCompletedShifts}
                    className="h-4 w-7"
                    data-testid="hide-completed-shifts-toggle"
                  />
                  <Label htmlFor="hide-completed-shifts" className="text-xs text-muted-foreground cursor-pointer whitespace-nowrap">
                    Hide Completed
                  </Label>
                </div>
                
                {/* Toggle B: Hide Archived Projects */}
                <div className="flex items-center gap-2">
                  <Switch
                    id="hide-archived-projects"
                    checked={hideArchivedProjects}
                    onCheckedChange={handleToggleHideArchivedProjects}
                    className="h-4 w-7"
                    data-testid="hide-archived-projects-toggle"
                  />
                  <Label htmlFor="hide-archived-projects" className="text-xs text-muted-foreground cursor-pointer whitespace-nowrap">
                    Hide Archived
                  </Label>
                </div>
              </div>
              
              {/* Active filter indicator */}
              {(hideCompletedShifts || hideArchivedProjects) && filteredShifts.length !== shifts.length && (
                <span className="text-[10px] text-muted-foreground italic">
                  Showing {hideArchivedProjects && hideCompletedShifts ? "active projects & shifts" : hideArchivedProjects ? "active projects only" : "active shifts only"}
                </span>
              )}
              
              {/* Clipboard indicator */}
              {crewClipboard && (
                <Badge 
                  variant="secondary" 
                  className="text-xs gap-1 bg-primary/10 text-primary"
                >
                  <Clipboard className="h-3 w-3" />
                  {crewClipboard.crew.length} crew copied
                </Badge>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              className="h-7"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Refresh
            </Button>
          </div>

          {/* Shifts list */}
          <ScrollArea className="flex-1 p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredShifts.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground">
                {hideCompletedShifts && shifts.length > 0 
                  ? "No upcoming shifts. Disable 'Hide Completed' to see all."
                  : "No shifts in this project"}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredShifts.map((shift) => {
                  const crew = shiftCrewMap[shift.id] || [];
                  const meals = shiftMealsMap[shift.id] || [];
                  const crewChiefRequired = getCrewChiefRequired(crew.length);
                  const crewChiefCount = getCrewChiefCount(crew);

                  return (
                    <ShiftRow
                      key={shift.id}
                      shift={shift}
                      crew={crew}
                      meals={meals}
                      crewChiefRequired={crewChiefRequired}
                      crewChiefCount={crewChiefCount}
                      onRemoveCrew={(staffId) =>
                        handleRemoveCrew(shift.id, staffId)
                      }
                      onToggleCrewChief={(staffId, makeCrewChief) =>
                        handleToggleCrewChief(shift.id, staffId, makeCrewChief)
                      }
                      onEdit={() => onEditShift(shift)}
                      onDelete={() => onDeleteShift(shift)}
                      onClone={() => onCloneShift(shift)}
                      onCopyCrew={() => onCopyCrew(shift.id)}
                      onPasteCrew={() => onPasteCrew(shift.id)}
                      hasClipboard={!!crewClipboard}
                      isOver={overShiftId === shift.id}
                      formatTime={formatTime}
                      formatDuration={formatDuration}
                    />
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>

      {/* Drag overlay */}
      <DragOverlay>
        {activeCrew && (
          <div className="flex items-center gap-2 px-2 py-1.5 rounded text-sm bg-primary/10 border border-primary/30 shadow-lg">
            <User className="h-3.5 w-3.5" />
            <span>
              {activeCrew.first_name} {activeCrew.last_name}
            </span>
          </div>
        )}
      </DragOverlay>
      
      {/* EF-CREW-OVERLAP-PREVENTION-V1: Conflict Dialog */}
      <ShiftConflictDialog
        isOpen={conflictDialogOpen}
        onClose={handleCloseConflictDialog}
        conflictData={conflictData}
        crewName={pendingAssignment?.staff ? `${pendingAssignment.staff.first_name} ${pendingAssignment.staff.last_name}` : ''}
        onOverride={handleConflictOverride}
        isSubmitting={conflictSubmitting}
      />

      {/* EF-PLANNER-VISIBILITY-AND-OVERRIDE-FIX-V2: Onboarding Override Confirmation Dialog */}
      <Dialog open={onboardingOverrideDialogOpen} onOpenChange={setOnboardingOverrideDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Assign Crew Member with Incomplete Onboarding?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-3">
              <p>
                <span className="font-medium text-foreground">
                  {pendingOnboardingOverride?.errorData?.crew_name || 
                   `${pendingOnboardingOverride?.staff?.first_name} ${pendingOnboardingOverride?.staff?.last_name}`}
                </span>{" "}
                has not completed onboarding (missing documents).
              </p>
              <p className="text-amber-600 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded-md">
                Reason: {pendingOnboardingOverride?.errorData?.reason || 
                         pendingOnboardingOverride?.errorData?.message?.replace('Crew member is not active and cannot be assigned. Reason: ', '') || 
                         'Missing required documents'}
              </p>
              <p>
                Do you want to override this requirement and assign them anyway?
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={handleCloseOnboardingOverrideDialog}
              disabled={onboardingOverrideSubmitting}
            >
              Cancel
            </Button>
            <Button 
              variant="default"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleOnboardingOverride}
              disabled={onboardingOverrideSubmitting}
              data-testid="confirm-onboarding-override-btn"
            >
              {onboardingOverrideSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Assign Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DndContext>
  );
}

export default ShiftPlannerView;
